































































































































import Vue from 'vue';
import { Grid } from '@progress/kendo-vue-grid';
import { CompositeFilterDescriptor, SortDescriptor, filterBy, orderBy } from '@progress/kendo-data-query';
import CheckBox from '@/components/CheckBox.vue';
import DateInput from '@/components/DateInput.vue';
import YellowConfirmation from '@/components/YellowConfirmation.vue';
import SpecialDimensionFilterButton from '@/components/SpecialDimensionFilterButton.vue';
import SpecialDimensionFilterValue, { emptySpecialDimensionFilter } from '@/modules/specialDimension/models/SpecialDimensionFilter';

const SEARCH_FIELDS = ['accno', 'accEvent', 'partnerName', 'docNo', 'remark', 'note', 'accDate', 'otherReconCode',
  'specDim1', 'specDim2', 'specDim3', 'specDim4'];

type SpecDimKey = keyof SpecialDimensionFilterValue;

// K2D w_sheet_trans sqlpreview: dimenziónként Tól <= érték <= Ig (üres fél = nincs korlát), a 4 dimenzió
// ÉS-elve; a tétel akkor talál, ha a FEJ értékei mind illeszkednek, VAGY a TÉTEL értékei mind illeszkednek.
function specDimMatches(values: string[], f: SpecialDimensionFilterValue): boolean {
  for (let d = 1; d <= 4; d++) {
    const from = (f[('from' + d) as SpecDimKey] || '').trim();
    const to = (f[('to' + d) as SpecDimKey] || '').trim();
    if (!from && !to) continue;
    const v = (values[d - 1] || '').trim();
    if (from && v < from) return false;
    if (to && v > to) return false;
  }
  return true;
}

function hasSpecDimFilter(f: SpecialDimensionFilterValue): boolean {
  return Object.values(f).some((x) => !!(x || '').trim());
}

// A DateInput felhasználói interakció (gépelés/naptár) UTÁN mindig Date objektumot emittál, NEM
// stringet (a kezdeti string kötés csak a nyitó értékre igaz) -- a dateFrom/dateTo string<->Date
// összehasonlítása enélkül NaN-ra/hamis eredményre fut, és minden szerkesztés után 0 találatra
// törné a szűrést. "YYYY-MM-DD"-re normalizálva mindkét bemeneti alak összehasonlítható marad.
function toDateStr(v: string | Date | null | undefined): string {
  if (!v) return '';
  if (typeof v === 'string') return v.slice(0, 10);
  if (v instanceof Date && !isNaN(v.getTime())) {
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, '0');
    const d = String(v.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return '';
}

interface Data {
  search: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  notOtherRecon: boolean;
  specDimFilter: SpecialDimensionFilterValue;
  selectedIds: number[];
  filter: CompositeFilterDescriptor;
  sort: SortDescriptor[];
  skip: number;
  take: number;
  pageable: any;
}

interface Methods {
  toggleSelect(item: any): Promise<void>;
  selectAll(): void;
  clearSelection(): void;
  add(): void;
  close(): void;
  reset(): void;
  filterChange(e: { filter: CompositeFilterDescriptor }): void;
  sortChange(e: { sort: SortDescriptor[] }): void;
  pageChange(e: any): void;
  rowRender(h: any, trElement: any, defaultSlots: any, props: any): any;
  formatTotal(value: any): string;
}

interface Computed {
  filteredItems: any[];
  pageItems: any[];
  selectedTotal: number;
  columns: any[];
}

interface Props {
  value: boolean;
  side: string;
  items: any[];
  year: number | null;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: 'GeneralLedgerMatchingItemPicker',

  components: {
    Grid,
    CheckBox,
    DateInput,
    YellowConfirmation,
    SpecialDimensionFilterButton,
  },

  props: {
    value: { type: Boolean, default: false },
    side: { type: String, default: 'positive' },
    items: { type: Array as () => any[], default: () => [] },
    year: { type: Number as () => number | null, default: null },
  },

  data(): Data {
    return {
      search: null,
      dateFrom: null,
      dateTo: null,
      notOtherRecon: false,
      specDimFilter: emptySpecialDimensionFilter(),
      selectedIds: [],
      filter: { logic: 'and', filters: [] },
      sort: [],
      skip: 0,
      take: 20,
      pageable: {
        buttonCount: 5,
        info: true,
        type: 'numeric',
        pageSizes: [20, 50, 100],
        previousNext: true,
      },
    };
  },

  watch: {
    // Nyitáskor tiszta állapot: a K2D ablak is a pontozás könyvelési évével (id_from/id_to)
    // előtöltött dátum-intervallummal, üres query-sorral nyílik
    // immediate: a gazda v-if-fel mountolja a panelt nyitáskor, ekkor a sima watcher nem sülne el
    value: {
      immediate: true,
      handler(open: boolean) {
        if (open) {
          this.reset();
          this.$nextTick(() => {
            const field = this.$refs.searchField as any;
            if (field && field.focus) field.focus();
          });
        }
      },
    },

    // A szűrők változásakor az első oldalra; a rejtett (kiszűrt) tétel kijelölése is megszűnik,
    // hogy ne adjunk hozzá olyat, amit a felhasználó már nem lát
    filteredItems(items: any[]) {
      this.skip = 0;
      const visible = new Set(items.map((i: any) => i.id));
      this.selectedIds = this.selectedIds.filter((id) => visible.has(id));
    },
  },

  methods: {
    reset() {
      this.search = null;
      this.dateFrom = this.year ? `${this.year}-01-01` : null;
      this.dateTo = this.year ? `${this.year}-12-31` : null;
      this.notOtherRecon = false;
      this.specDimFilter = emptySpecialDimensionFilter();
      this.selectedIds = [];
      this.filter = { logic: 'and', filters: [] };
      this.sort = [];
      this.skip = 0;
    },

    async toggleSelect(item) {
      const idx = this.selectedIds.indexOf(item.id);
      if (idx !== -1) {
        this.selectedIds.splice(idx, 1);
        return;
      }

      // K2D w_popup_tetel_valaszt ue_check: ha a tétel a MÁSIK mód összepontozásában már
      // szerepel (keresztmező), rákérdezünk — csak figyelmeztetés, Igen-re mehet tovább.
      if (item.otherReconId > 1) {
        const ok = await (this.$refs.crossModeConfirm as any).show(
          `Ez a tétel már egy másik összepontozásban szerepel. ID: ${item.otherReconId} ` +
          `pontozószám: ${String(item.otherReconCode || '').trim()}. Biztos, hogy kiválasztja?`
        );
        if (!ok) return;
      }

      this.selectedIds.push(item.id);
    },

    // K2D cb_plus_treff (§): a szűrt lista MINDEN sora (nem csak az aktuális oldal), rákérdezés nélkül
    selectAll() {
      this.selectedIds = this.filteredItems.map((i: any) => i.id);
    },

    // K2D cb_plus_treff_0 (-)
    clearSelection() {
      this.selectedIds = [];
    },

    add() {
      const selected = this.items.filter((i: any) => this.selectedIds.includes(i.id));
      if (selected.length === 0) return;
      this.$emit('add', selected);
      this.$emit('input', false);
    },

    close() {
      this.$emit('input', false);
    },

    filterChange(e) {
      this.filter = e.filter || { logic: 'and', filters: [] };
    },

    sortChange(e) {
      this.sort = e.sort;
    },

    pageChange(e) {
      this.skip = e.page.skip;
      this.take = e.page.take;
    },

    // K2D d_g_tetel_valaszt: a másik mód pontozásában lévő tételek halványzöld háttere.
    rowRender(h, trElement, defaultSlots, props) {
      const trProps = { style: props.dataItem.otherReconId > 1 ? 'backgroundColor: rgb(204,255,204)' : '' };
      return h('tr', trProps, defaultSlots);
    },

    formatTotal(value) {
      return new Intl.NumberFormat('hu-HU').format(Number(value || 0));
    },
  },

  computed: {
    // Sorrend: dátum-intervallum + pipa + gyorskereső (saját), majd a rács oszlop-szűrői és rendezése (Kendo)
    filteredItems(): any[] {
      let result = this.items as any[];

      const from = toDateStr(this.dateFrom);
      const to = toDateStr(this.dateTo);
      if (from) {
        result = result.filter((i: any) => i.accDate >= from);
      }
      if (to) {
        result = result.filter((i: any) => i.accDate <= to);
      }
      if (this.notOtherRecon) {
        result = result.filter((i: any) => !(i.otherReconId > 1));
      }

      // Spec dim szűrő a többi szűrővel ÉS-elve (tudatos eltérés a K2D böngészőtől, ahol kizárja a többit)
      if (hasSpecDimFilter(this.specDimFilter)) {
        const f = this.specDimFilter;
        result = result.filter((i: any) =>
          specDimMatches([i.headSpecDim1, i.headSpecDim2, i.headSpecDim3, i.headSpecDim4], f) ||
          specDimMatches([i.specDim1, i.specDim2, i.specDim3, i.specDim4], f));
      }

      const term = (this.search || '').trim().toLowerCase();
      if (term) {
        result = result.filter((i: any) =>
          SEARCH_FIELDS.some((f) => String(i[f] ?? '').toLowerCase().includes(term)) ||
          String(i.total ?? '').includes(term) ||
          String(i.trans ?? '') === term);
      }

      return orderBy(filterBy(result, this.filter), this.sort);
    },

    pageItems(): any[] {
      return this.filteredItems.slice(this.skip, this.skip + this.take);
    },

    selectedTotal(): number {
      return (this.items as any[])
        .filter((i: any) => this.selectedIds.includes(i.id))
        .reduce((sum: number, i: any) => sum + Number(i.total || 0), 0);
    },

    // Oszlopok a K2D d_g_tetel_valaszt sorrendje szerint (autó/rendszám/alvázszám/város/cím nélkül)
    columns(): any[] {
      return [
        {
          field: 'selected',
          title: ' ',
          width: '50px',
          cell: 'selectCell',
          // A Kendo a szűrő-cellát maga teszi <th>-be; saját filterCell-sablon helyett filterable:false
          // (a sablon <td>-je a <th>-ben egy árva vonalat rajzolt)
          filterable: false,
          sortable: false,
          headerClassName: 'center_text',
        },
        { field: 'accno', title: 'Főkönyvi szám', width: '120px' },
        { field: 'total', title: 'Összeg', width: '130px', cell: 'totalCell', filter: 'numeric', headerClassName: 'right_text' },
        { field: 'partnerName', title: 'Partner', width: '200px' },
        { field: 'accDate', title: 'Gazd. es. dátuma', width: '140px', className: 'right_text', headerClassName: 'right_text' },
        { field: 'trans', title: 'Tranz. szám', width: '110px', filter: 'numeric' },
        { field: 'accEvent', title: 'Gazd. esemény', width: '160px' },
        { field: 'docNo', title: 'Bizonylatszám', width: '150px' },
        { field: 'remark', title: 'Megjegyzés' },
        { field: 'note', title: 'Leírás' },
        { field: 'otherReconCode', title: 'Pontozószám', width: '130px' },
        // Tétel spec dim oszlopok (K2D d_g_ixboktrans "Spec Dim1..4"; TF-feliratok a tranzakció-szerkesztővel egyezően)
        { field: 'specDim1', title: 'Munkaszám', width: '110px' },
        { field: 'specDim2', title: 'Gépjármű', width: '110px' },
        { field: 'specDim3', title: 'Szerelő', width: '110px' },
        { field: 'specDim4', title: 'Szerviztípus', width: '110px' },
        { filterable: false, sortable: false, width: 0 },
      ];
    },
  },
});
